/**
 * WhyBadge Component
 *
 * Displays contextual "why" information for training items.
 * Tiered messaging based on how the training was generated:
 *
 * - AI-generated with gap data: "Your [SOP] scored [currentLabel]. Target: [targetLabel]."
 * - AI-generated with gap description only: Shows the gap description text
 * - AI-generated without gap data: "AI training for [SOP name]"
 * - Manual with SOP: "Assigned for [SOP name]"
 * - Manual without SOP: "Assigned by your team"
 *
 * Two variants:
 * - compact: single-line pill for training cards
 * - expanded: multi-line card for detail/viewer page
 */

import { Sparkles, BookOpen } from 'lucide-react';

interface WhyBadgeProps {
  generatedBy: string;
  sopName: string | null;
  gapDescription: string | null;
  currentScoreLabel: string | null;
  targetScoreLabel: string | null;
  /** Compact mode for training cards, expanded for detail page */
  variant?: 'compact' | 'expanded';
}

export function WhyBadge({
  generatedBy,
  sopName,
  gapDescription,
  currentScoreLabel,
  targetScoreLabel,
  variant = 'compact',
}: WhyBadgeProps) {
  const isAiGenerated = generatedBy !== 'manual';

  // Determine the message text based on available context
  const getMessage = (): string => {
    if (isAiGenerated) {
      // AI-generated with score labels
      if (currentScoreLabel && targetScoreLabel && sopName) {
        return `Your ${sopName} scored ${currentScoreLabel}. Target: ${targetScoreLabel}.`;
      }
      // AI-generated with gap description but no scores
      if (gapDescription) {
        return gapDescription;
      }
      // AI-generated with SOP name but no gap data
      if (sopName) {
        return `AI training for ${sopName}`;
      }
      // AI-generated with no context at all
      return 'AI-recommended training';
    }

    // Manual assignment
    if (sopName) {
      return `Assigned for ${sopName}`;
    }
    return 'Assigned by your team';
  };

  const message = getMessage();

  if (variant === 'compact') {
    return (
      <div
        className={`mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${
          isAiGenerated
            ? 'bg-amber-50 text-amber-700'
            : 'bg-gray-100 text-gray-600'
        }`}
      >
        {isAiGenerated ? (
          <Sparkles size={12} strokeWidth={1.5} className="flex-shrink-0" />
        ) : (
          <BookOpen size={12} strokeWidth={1.5} className="flex-shrink-0" />
        )}
        <span className="line-clamp-1">{message}</span>
      </div>
    );
  }

  // Expanded variant for detail page
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${
          isAiGenerated
            ? 'bg-amber-100 text-amber-600'
            : 'bg-gray-100 text-gray-500'
        }`}
      >
        {isAiGenerated ? (
          <Sparkles size={18} strokeWidth={1.5} />
        ) : (
          <BookOpen size={18} strokeWidth={1.5} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-op-text-primary">{message}</p>
        {isAiGenerated && currentScoreLabel && targetScoreLabel && (
          <div className="mt-1.5 flex items-center gap-2 text-xs text-op-text-tertiary">
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-red-600">
              Current: {currentScoreLabel}
            </span>
            <span className="text-op-text-tertiary">→</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-600">
              Target: {targetScoreLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
